import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Video, Users, Package, Trash2, Plus, Search, ChevronRight, Clapperboard } from "lucide-react";
import type { Persona, Product, DayinlifeCampaign } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  scripting: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
  producing: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  complete: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  completed: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  archived: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
};


export default function DayinlifeCampaignsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<DayinlifeCampaign | null>(null);
  const [search, setSearch] = useState("");

  const { data: dayinlifeCampaigns, isLoading } = useQuery<DayinlifeCampaign[]>({
    queryKey: ["/api/brands", brandId, "dayinlife-campaigns"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/dayinlife-campaigns`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch Day in the Life campaigns");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/dayinlife-campaigns/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "dayinlife-campaigns"] });
      toast({ title: "Day in the Life campaign deleted" });
      setDeleteTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Failed to delete campaign", description: e.message, variant: "destructive" });
    },
  });

  const personaMap = new Map(personas?.map((p) => [p.id, p]) || []);
  const productMap = new Map(products?.map((p) => [p.id, p]) || []);

  const filtered = dayinlifeCampaigns?.filter(c => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }) ?? [];

  return (
    <>
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-dayinlife-campaigns-title">AI Day in the Life Campaigns</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Create AI-generated Day in the Life video scripts and content</p>
          </div>
          <Button onClick={() => navigate(`/brands/${brandId}/dayinlife-campaigns/new`)} className="rounded-full px-5" data-testid="button-create-dayinlife-campaign">
            <Plus className="h-4 w-4 mr-1.5" /> New Day in the Life Campaign
          </Button>
        </div>

        <div className="flex items-center gap-3 flex-wrap fade-in-up-delay-1">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search Day in the Life campaigns..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="input-search-dayinlife-campaigns"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid gap-3 fade-in-up-delay-2" data-testid="grid-dayinlife-campaigns">
            {filtered.map((campaign) => {
              const persona = campaign.personaId ? personaMap.get(campaign.personaId) : null;
              const product = campaign.productId ? productMap.get(campaign.productId) : null;
              const styleLabel = campaign.videoStyle;

              return (
                <Card
                  key={campaign.id}
                  className="border-border/40 hover:border-violet-500/30 cursor-pointer transition-all group"
                  onClick={() => navigate(`/brands/${brandId}/dayinlife-campaigns/${campaign.id}`)}
                  data-testid={`card-dayinlife-campaign-${campaign.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3.5 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Clapperboard className="h-5 w-5 text-violet-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold leading-snug line-clamp-1" data-testid={`text-dayinlife-name-${campaign.id}`}>
                              {campaign.name}
                            </p>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0 ${statusColors[campaign.status] || statusColors.draft}`}>
                              {campaign.status}
                            </span>
                          </div>
                          {campaign.concept && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{campaign.concept}</p>
                          )}
                          <div className="flex items-center gap-3 flex-wrap">
                            {persona && (
                              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <Users className="h-3 w-3 text-violet-500/60" />
                                <span className="truncate max-w-[120px]">{persona.name}</span>
                              </div>
                            )}
                            {product && (
                              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <Package className="h-3 w-3 text-blue-500/60" />
                                <span className="truncate max-w-[120px]">{product.name}</span>
                              </div>
                            )}
                            {campaign.videoStyle && (
                              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <Video className="h-3 w-3 text-amber-500/60" />
                                <span>{styleLabel}</span>
                              </div>
                            )}
                            {campaign.duration && (
                              <span className="text-[11px] text-muted-foreground/60">{campaign.duration}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/30 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteTarget(campaign);
                          }}
                          data-testid={`button-delete-dayinlife-${campaign.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-dashed border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <div className="w-14 h-14 rounded-2xl bg-violet-500/10 flex items-center justify-center mb-5">
                <Clapperboard className="h-7 w-7 text-violet-500" />
              </div>
              <h3 className="font-semibold tracking-tight mb-1.5 text-lg">No Day in the Life campaigns yet</h3>
              <p className="text-sm text-muted-foreground mb-1 text-center max-w-sm">
                Create AI-generated Day in the Life video scripts with hooks, talking points, and CTAs tailored to your brand personas.
              </p>
              <p className="text-xs text-muted-foreground/60 mb-6 text-center max-w-sm">
                Choose a video style, assign a persona and product, then generate creator-ready scripts.
              </p>
              <Button onClick={() => navigate(`/brands/${brandId}/dayinlife-campaigns/new`)} className="rounded-full px-6" data-testid="button-create-first-dayinlife">
                <Plus className="h-4 w-4 mr-1.5" /> New Day in the Life Campaign
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {deleteTarget && (
        <Dialog open onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setDeleteTarget(null); }}>
          <DialogContent data-testid="dialog-delete-dayinlife-campaign">
            <DialogHeader>
              <DialogTitle>Delete Day in the Life Campaign</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will permanently remove the campaign and all its scripts. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                data-testid="button-cancel-delete-dayinlife"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                data-testid="button-confirm-delete-dayinlife"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
